import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil
} from '@cannect/assets/img'
import { useAuth } from '@cannect/hooks/AuthContext'
import { useUnavailableItems } from '@cannect/hooks/useUnavailableItems'
import UnavailableProductModal from '@cannect/new-components/UnavailableProductModal'
import api from '@cannect/services/api'
import { BRANDS_WITH_REQUIRED_FREIGHT, calculateShippingCost } from '@cannect/utils/constants'
import { clearMask, formatReal } from '@cannect/utils/formatters'
import Modal from 'new-components/Modal'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { toast } from 'react-toastify'
import { isProgramOrder } from 'pages/WeightLossProgram/constants'
import { useFinalizeCheckout } from '../../../../../hooks/useFinalizeCheckout'
import { usePrepareCart } from '../../../../../hooks/usePrepareCart'
import useQueryString from '../../../../../hooks/useQueryString'
import { useTemporaryCvv } from '../../../../../hooks/useTemporaryCvv'
import { CouponSection, PaymentMethods } from '../../../../../new-components/CartSheet/components'
import PaymentModal from '../../../../../new-components/PaymentModal'
import { listSavedCards } from '../../../../../services/cards'
import { getEvents } from '../../../../../services/events'
import { paymentService } from '../../../../../services/paymentService'
import { Sumary } from '../Sumary/Sumary'
import { BoletoPayment } from './BoletoPayment'
import { PixPayment } from './PixPayment'
import CaptureCvvModal from './components/CaptureCvvModal'

/**
 * Sinaliza que o erro foi um 422 de produto indisponível já tratado
 * (itens removidos + modal aberto), para não exibir o erro genérico de pagamento.
 */
class UnavailableItemsError extends Error {
  constructor() {
    super('Produtos indisponíveis removidos do carrinho')
    this.name = 'UnavailableItemsError'
  }
}

interface CartItem {
  id: number
  type?: string
  product?: {
    name?: string
    flavor?: string
    current_price?: number
    price?: number
    real_price?: number
    type?: string
    media?: Array<{
      path: string
    }>
    currency?: string
  }
  quantity?: number
  amount?: string
  discount?: string
}

interface OrderSummaryStep3Props {
  itens: CartItem[]
  importCost?: number
  onApplyCoupon?: (coupon: any, itens: CartItem[]) => void
  isUpdate: boolean
  currentStep: number
  onNextStep?: () => void
  onPreviousStep?: () => void
  addressData?: any
  selectedPaymentMethod?: string
  coupon: any
  onRemoveCoupon: () => void
}

export const OrderSummaryStep3: React.FC<OrderSummaryStep3Props> = ({
  itens,
  importCost,
  onApplyCoupon,
  isUpdate,
  currentStep,
  onNextStep,
  onPreviousStep: _onPreviousStep,
  addressData,
  selectedPaymentMethod,
  coupon,
  onRemoveCoupon
}) => {
  const { loading, lastOrderCreated, deliveryCep, validatingDeliveryCep, setOrderData } = usePrepareCart() as any
  const { filters } = useQueryString()
  const isPublicCart = Boolean(filters?.cart_id)
  const hasUnavailableItems = !!(lastOrderCreated?.summary?.has_unavailable_items ?? lastOrderCreated?.has_unavailable_items)
  const shippingRestriction =
    lastOrderCreated?.shipping_restriction ?? lastOrderCreated?.summary?.shipping_restriction ?? null
  const tazBlocked =
    shippingRestriction !== null &&
    (shippingRestriction.covered === false || (shippingRestriction.requires_cep && !deliveryCep))
  const { finalize } = useFinalizeCheckout()
  const { temporaryCvv, clearCvv } = useTemporaryCvv()
  const {
    unavailableProducts,
    isUnavailableModalOpen,
    isRemoving,
    handleUnavailableError,
    closeModal: closeUnavailableModal
  } = useUnavailableItems()

  const calculatedImportCost = importCost ?? calculateShippingCost(lastOrderCreated?.items || [])
  const [savingAddress, setSavingAddress] = useState(false)
  const [showCvv, setShowCvv] = useState(false)
  const [isPaying, setIsPaying] = useState(false)
  const [_payError, setPayError] = useState<string | null>(null)
  const [showPixModal, setShowPixModal] = useState(false)
  const [showBoletoModal, setShowBoletoModal] = useState(false)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<'processing' | 'success' | 'error'>('processing')
  const [hasSavedCards, setHasSavedCards] = useState(false) // Começa como false até verificar
  const [isCheckingCards, setIsCheckingCards] = useState(true) // Estado de loading
  const form = useFormContext<{ cvv1: string; cvv2: string; cvv_card_pix: string; cvv_card_boleto: string }>()
  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      item?.product?.class_id === 6 && !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  const isAllGeneticTests = lastOrderCreated?.items.every((item: any) => item?.product?.type === 'TESTE_GENETICO')

  const isPillOnly: boolean = lastOrderCreated?.is_pill_only ?? false
  const hasPillControlledItems = (lastOrderCreated?.items || []).some(
    (item: any) => item.requires_controlled_prescription
  )
  const skipDocuments =
    isAllSupplements ||
    isAllGeneticTests ||
    (isPillOnly && !hasPillControlledItems) ||
    isProgramOrder(lastOrderCreated)

  const showPaymentStatusModal = (status: 'processing' | 'success' | 'error') => {
    setPaymentStatus(status)
    setShowPaymentModal(true)
  }

  useEffect(() => {
    const checkSavedCards = async () => {
      try {
        setIsCheckingCards(true)
        const cards = await listSavedCards()
        setHasSavedCards(cards && cards.length > 0)
      } catch {
        setHasSavedCards(false)
      } finally {
        setIsCheckingCards(false)
      }
    }
    checkSavedCards()
  }, [])

  useEffect(() => {
    const checkSavedCards = async () => {
      try {
        const cards = await listSavedCards()
        setHasSavedCards(cards && cards.length > 0)
      } catch {
        setHasSavedCards(false)
      }
    }

    const handleCardAdded = () => {
      checkSavedCards()
    }

    window.addEventListener('cardAdded', handleCardAdded)

    return () => {
      window.removeEventListener('cardAdded', handleCardAdded)
    }
  }, [])

  const handlePaymentModalAction = async () => {
    if (paymentStatus === 'success') {
      setShowPaymentModal(false)
      await finalize({
        redirectTo:
          skipDocuments
            ? `/detalhes-do-pedido/${lastOrderCreated.id}`
            : `/enviar-receita/${lastOrderCreated.id}`,
        shouldClearCart: true,
        alsoClearOrderState: true
      })
    } else if (paymentStatus === 'error') {
      setShowPaymentModal(false)
    }
  }

  const { role } = useAuth()

  // Removido canSubmitTwoCards, canSubmitCardPix e canSubmitCardBoleto
  // As validações agora são feitas via toast no clique do botão

  const validItems = itens.filter((item: any) => (item.quantity || 0) > 0)

  const subtotal = validItems.reduce((acc: number, item: any) => acc + parseFloat(item.amount || '0'), 0)

  const productDiscounts = validItems.reduce((acc: number, item: any) => {
    const itemDiscount = parseFloat(item.discount || '0')

    if (itemDiscount === 0 && item.product?.price && item.product?.real_price) {
      const originalPrice = item.product.price * (item.quantity || 0)
      const finalPrice = item.product.real_price * (item.quantity || 0)
      const calculatedDiscount = Math.max(0, originalPrice - finalPrice)
      return acc + calculatedDiscount
    }

    return acc + itemDiscount
  }, 0)

  const total = subtotal + calculatedImportCost - productDiscounts
  const freeShippingThreshold = 2300
  const remainingForFreeShipping = Math.max(0, freeShippingThreshold - total)

  const importDiscount = remainingForFreeShipping <= 0 ? calculatedImportCost : 0
  const finalTotal = subtotal + calculatedImportCost - productDiscounts - importDiscount

  const applyPixDiscount =
    import.meta.env.VITE_APP_APPLY_PIX_DISCOUNT === 'enabled' && lastOrderCreated?.apply_pix_discount
  const pixDiscountPercentage = Number(import.meta.env.VITE_APP_PIX_DISCOUNT_PERCENTAGE)

  const applyPaymentDiscount = (amount: number) => {
    if (applyPixDiscount) {
      const discount = amount * (pixDiscountPercentage / 100)
      return amount - discount
    }
    return amount
  }

  const createPayment = async (payments: any[]) => {
    // Aplica desconto PIX quando necessário
    const processedPayments = payments.map((payment) => {
      if (payment.paymentMethod.type === 'pix' && applyPixDiscount && selectedPaymentMethod !== 'card_pix') {
        const originalAmount = payment.amount || (lastOrderCreated?.amount || 0) * 100
        const discountedAmount = Math.round(applyPaymentDiscount(originalAmount / 100) * 100)
        return {
          ...payment,
          amount: discountedAmount
        }
      }
      return payment
    })

    try {
      const { data } = await api.post('/payments/charges', {
        order_id: lastOrderCreated?.id,
        payments: processedPayments,
        utmParams: {}
      })
      if (!data?.success) throw new Error('Falha ao processar pagamento')
      return data
    } catch (error) {
      // 422 de estoque zerado: remove os itens, abre o modal e sinaliza para os
      // catch blocks abaixo não exibirem o erro genérico de pagamento.
      const handled = await handleUnavailableError(error)
      if (handled) throw new UnavailableItemsError()
      throw error
    }
  }

  const handlePay = async (cvv: string) => {
    try {
      if (!lastOrderCreated?.id) throw new Error('Pedido não encontrado')
      const stored = localStorage.getItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
      const cardExternalId = stored ? String(stored) : null
      if (!cardExternalId) throw new Error('Selecione um cartão salvo')
      setIsPaying(true)
      setPayError(null)
      let installments = 1
      try {
        const raw = localStorage.getItem('@CANNECT:CREDIT_SELECTION')
        const sel = raw ? JSON.parse(raw) : {}
        installments = Number(sel.installments || 1)
      } catch {
        /* ignore */
      }
      // Envia valor SEM juros - o backend calcula os juros automaticamente
      const baseTotal = Number((lastOrderCreated?.amount as number) || finalTotal)
      // Mostrar modal de processamento
      showPaymentStatusModal('processing')

      await paymentService.generateSavedCardCharge(lastOrderCreated.id, cardExternalId, cvv, baseTotal, installments)

      showPaymentStatusModal('success')

      getEvents.eventLoadObjects({
        name: 'checkout_redirect',
        category: 'checkout',
        action: 'card_paid_redirect',
        value: String(lastOrderCreated.id)
      })
      getEvents.eventSendDataLayer({
        eventName: 'checkout_redirect',
        method: 'credit_card',
        trigger: 'card_approved',
        orderId: lastOrderCreated.id,
        target: `/enviar-receita/${lastOrderCreated.id}`
      })
    } catch (e) {
      // `handlePay` não passa por `createPayment`, então o 422 é tratado aqui.
      if (await handleUnavailableError(e)) {
        setShowPaymentModal(false)
        return
      }
      const msg = e instanceof Error ? e.message : 'Erro ao processar pagamento'
      setPayError(msg)
      showPaymentStatusModal('error')
      toast.error(msg)
    } finally {
      setIsPaying(false)
    }
  }

  const validatePaymentForm = () => {
    // Validações comuns para todos os métodos
    if (!hasSavedCards) {
      toast.error('Adicione um cartão para efetuar o pagamento')
      return false
    }

    // Validações específicas por método de pagamento
    if (selectedPaymentMethod === 'card_pix') {
      const raw = localStorage.getItem('@CANNECT:CARD_PIX_SELECTION')
      if (!raw) {
        toast.error('Selecione um cartão para o pagamento')
        return false
      }

      const sel = JSON.parse(raw)
      const cvv = form?.getValues?.('cvv_card_pix') || ''

      if (!sel?.selectedCardExternalId) {
        toast.error('Selecione um cartão para o pagamento')
        return false
      }

      if (!sel?.installments) {
        toast.error('Selecione a quantidade de parcelas')
        return false
      }

      if (!cvv) {
        toast.error('Informe o CVV do cartão')
        return false
      }
    }

    if (selectedPaymentMethod === 'card_boleto') {
      const raw = localStorage.getItem('@CANNECT:CARD_BOLETO_SELECTION')
      if (!raw) {
        toast.error('Selecione um cartão para o pagamento')
        return false
      }

      const sel = JSON.parse(raw)
      const cvv = form?.getValues?.('cvv_card_boleto') || ''

      if (!sel?.selectedCardExternalId) {
        toast.error('Selecione um cartão para o pagamento')
        return false
      }

      if (!sel?.installments) {
        toast.error('Selecione a quantidade de parcelas')
        return false
      }

      if (!cvv) {
        toast.error('Informe o CVV do cartão')
        return false
      }
    }

    if (selectedPaymentMethod === 'two_cards') {
      const raw = localStorage.getItem('@CANNECT:TWO_CARDS_SELECTION')
      if (!raw) {
        toast.error('Selecione os cartões e preencha os dados')
        return false
      }

      const sel = JSON.parse(raw)

      if (!sel.selectedCardExternalId1 || !sel.selectedCardExternalId2) {
        toast.error('Selecione os dois cartões para o pagamento')
        return false
      }

      if (!sel.installments1) {
        toast.error('Selecione a quantidade de parcelas para o primeiro cartão')
        return false
      }

      if (!sel.installments2) {
        toast.error('Selecione a quantidade de parcelas para o segundo cartão')
        return false
      }

      const cvv1 = form?.getValues?.('cvv1') || ''
      const cvv2 = form?.getValues?.('cvv2') || ''

      if (!cvv1) {
        toast.error('Informe o CVV do primeiro cartão')
        return false
      }

      if (!cvv2) {
        toast.error('Informe o CVV do segundo cartão')
        return false
      }
    }

    if (selectedPaymentMethod === 'credit') {
      const selectedCardId = localStorage.getItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
      if (!selectedCardId) {
        toast.error('Selecione um cartão para o pagamento')
        return false
      }

      const raw = localStorage.getItem('@CANNECT:CREDIT_SELECTION')
      if (!raw) {
        toast.error('Selecione a quantidade de parcelas')
        return false
      }

      const sel = JSON.parse(raw)

      if (!sel?.installments) {
        toast.error('Selecione a quantidade de parcelas')
        return false
      }
    }

    return true
  }

  const handlePayClick = async () => {
    if (!lastOrderCreated?.id) return

    // Executa validações antes de prosseguir
    if (!validatePaymentForm()) {
      return
    }

    console.log('selectedPaymentMethod', selectedPaymentMethod)

    if (selectedPaymentMethod === 'card_pix') {
      try {
        setIsPaying(true)
        setPayError(null)

        const raw = localStorage.getItem('@CANNECT:CARD_PIX_SELECTION')
        const sel = JSON.parse(raw!)

        const cvv = form?.getValues?.('cvv_card_pix') || ''
        const cardExternalId = sel?.selectedCardExternalId
        const installments = sel?.installments || 1
        const percentCard = Number(sel?.amountCard !== undefined && sel?.amountCard !== null ? sel.amountCard : 50)
        // const percentPix = Number(sel?.amountPix !== undefined && sel?.amountPix !== null ? sel.amountPix : 50)

        const totalBase = (lastOrderCreated?.amount as number) || finalTotal
        const totalCents = Math.round(Number(totalBase) * 100)
        // Envia valor SEM juros - o backend calcula os juros automaticamente
        const cardPortionBRL = (Number(totalBase) * percentCard) / 100
        const cardValueCents = Math.round(Math.max(0, cardPortionBRL) * 100)
        const pixValueCents = Math.max(0, totalCents - cardValueCents)

        const payments = [
          {
            paymentMethod: { type: 'credit_card' },
            card_id: cardExternalId,
            cvv,
            installments,
            card_value: cardValueCents
          },
          {
            paymentMethod: { type: 'pix' },
            amount: pixValueCents
          }
        ]

        console.log('payments', payments)

        const data = await createPayment(payments)

        const pixResp = data?.responses?.find?.((r: any) => r?.payment_method === 'pix')
        const pixTx = pixResp?.last_transaction
        const prefetched = pixTx
          ? {
              id: pixResp?.id || '',
              gateway_id: pixTx.gateway_id || '',
              qr_code: pixTx.qr_code || '',
              qr_code_text: pixTx.qr_code_text || '',
              qr_code_url: pixTx.qr_code_url || '',
              expires_at: pixTx.expires_at || '',
              status: pixTx.status || 'pending',
              pix_provider_tid: pixTx.pix_provider_tid || ''
            }
          : null

        setShowPixModal(true)
        ;(window as any).PREFETCHED_PIX = prefetched
      } catch (e) {
        if (e instanceof UnavailableItemsError) return
        const msg = e instanceof Error ? e.message : 'Erro ao processar pagamento'
        setPayError(msg)
        toast.error(msg)
      } finally {
        setIsPaying(false)
      }
      return
    }

    if (selectedPaymentMethod === 'pix') {
      try {
        setIsPaying(true)
        setPayError(null)

        const totalBase = (lastOrderCreated?.amount as number) || finalTotal
        const finalAmount = applyPixDiscount ? applyPaymentDiscount(totalBase) : totalBase

        const payments = [
          {
            paymentMethod: { type: 'pix' },
            amount: Math.round(finalAmount * 100)
          }
        ]

        const data = await createPayment(payments)

        const pixResp = data?.responses?.find?.((r: any) => r?.payment_method === 'pix')
        const pixTx = pixResp?.last_transaction
        const prefetched = pixTx
          ? {
              id: pixResp?.id || '',
              gateway_id: pixTx.gateway_id || '',
              qr_code: pixTx.qr_code || '',
              qr_code_text: pixTx.qr_code_text || '',
              qr_code_url: pixTx.qr_code_url || '',
              expires_at: pixTx.expires_at || '',
              status: pixTx.status || 'pending',
              pix_provider_tid: pixTx.pix_provider_tid || ''
            }
          : null

        setShowPixModal(true)
        ;(window as any).PREFETCHED_PIX = prefetched
      } catch (e) {
        if (e instanceof UnavailableItemsError) return
        const msg = e instanceof Error ? e.message : 'Erro ao processar pagamento PIX'
        setPayError(msg)
        toast.error(msg)
      } finally {
        setIsPaying(false)
      }
      return
    }

    if (selectedPaymentMethod === 'card_boleto') {
      try {
        setIsPaying(true)
        setPayError(null)

        const raw = localStorage.getItem('@CANNECT:CARD_BOLETO_SELECTION')
        const sel = JSON.parse(raw!)

        const cvv = form?.getValues?.('cvv_card_boleto') || ''
        const cardExternalId = sel?.selectedCardExternalId
        const installments = sel?.installments || 1
        const percentCard = Number(sel?.amountCard !== undefined && sel?.amountCard !== null ? sel.amountCard : 50)
        // const percentBoleto = Number(
        //   sel?.amountBoleto !== undefined && sel?.amountBoleto !== null ? sel.amountBoleto : 50
        // )

        const totalBase = (lastOrderCreated?.amount as number) || finalTotal
        const totalCents = Math.round(Number(totalBase) * 100)
        // Envia valor SEM juros - o backend calcula os juros automaticamente
        const cardPortionBRL = (Number(totalBase) * percentCard) / 100
        const cardValueCents = Math.round(Math.max(0, cardPortionBRL) * 100)
        const boletoValueCents = Math.max(0, totalCents - cardValueCents)

        const payments = [
          {
            paymentMethod: { type: 'credit_card' },
            card_id: cardExternalId,
            cvv,
            installments,
            card_value: cardValueCents
          },
          {
            paymentMethod: { type: 'boleto' },
            amount: boletoValueCents
          }
        ]

        const data = await createPayment(payments)

        const bolResp = data?.responses?.find?.((r: any) => r?.payment_method === 'boleto')
        const bolTx = bolResp?.last_transaction
        const prefetched = bolTx
          ? {
              id: bolResp?.id || '',
              gateway_id: bolTx.gateway_id || '',
              boleto_barcode: bolTx.boleto_barcode || bolTx.line || bolTx.gateway_id || '',
              boleto_expiration_date: bolTx.boleto_expiration_date || bolTx.expires_at || bolTx.due_at || '',
              boleto_url: bolTx.boleto_url || bolTx.pdf || bolTx.url || '',
              status: bolTx.status || 'pending',
              qr_code: bolTx.qr_code,
              qr_code_url: bolTx.barcode || bolTx.qr_code_url
            }
          : null

        setShowBoletoModal(true)
        ;(window as any).PREFETCHED_BOLETO = prefetched
      } catch (e) {
        if (e instanceof UnavailableItemsError) return
        const msg = e instanceof Error ? e.message : 'Erro ao processar pagamento'
        setPayError(msg)
        toast.error(msg)
      } finally {
        setIsPaying(false)
      }
      return
    }

    if (selectedPaymentMethod === 'two_cards') {
      try {
        setIsPaying(true)
        setPayError(null)

        // Mostrar modal de processamento ANTES de processar
        showPaymentStatusModal('processing')
        const raw = localStorage.getItem('@CANNECT:TWO_CARDS_SELECTION')
        const sel = JSON.parse(raw!)

        const cvv1 = form?.getValues?.('cvv1') || ''
        const cvv2 = form?.getValues?.('cvv2') || ''

        // Envia valores SEM juros - o backend calcula os juros automaticamente
        const payments = [
          {
            paymentMethod: { type: 'credit_card' },
            card_id: sel.selectedCardExternalId1,
            cvv: cvv1,
            installments: sel.installments1 || 1,
            card_value: Math.round(Math.max(0, Number(sel.amountCard1)) * 100)
          },
          {
            paymentMethod: { type: 'credit_card' },
            card_id: sel.selectedCardExternalId2,
            cvv: cvv2,
            installments: sel.installments2 || 1,
            card_value: Math.round(Math.max(0, Number(sel.amountCard2)) * 100)
          }
        ]
        // payments debug
        const data = await createPayment(payments)

        if (!data?.success) throw new Error('Falha no processamento do pagamento')

        const allCardsPaid = (data?.responses || [])
          .filter((r: any) => r?.payment_method === 'credit_card')
          .every((r: any) => r?.last_transaction?.success)

        if (allCardsPaid) {
          showPaymentStatusModal('success')

          getEvents.eventLoadObjects({
            name: 'checkout_redirect',
            category: 'checkout',
            action: 'two_cards_paid_redirect',
            value: String(lastOrderCreated.id)
          })
          getEvents.eventSendDataLayer({
            eventName: 'checkout_redirect',
            method: 'two_cards',
            trigger: 'card_approved',
            orderId: lastOrderCreated.id,
            target: skipDocuments
              ? `/detalhes-do-pedido/${lastOrderCreated.id}`
              : `/enviar-receita/${lastOrderCreated.id}`
          })
        } else {
          const failedCards = (data?.responses || []).filter(
            (r: any) => r?.payment_method === 'credit_card' && !r?.last_transaction?.success
          )

          let errorMessage = 'Pagamento recusado'
          if (failedCards.length > 0) {
            const statuses = failedCards.map((card: any) => card?.last_transaction?.status).join(', ')
            errorMessage = `Pagamento recusado. Status: ${statuses}`
          }

          setPayError(errorMessage)
          showPaymentStatusModal('error')
          toast.error(errorMessage)
        }
      } catch (e) {
        if (e instanceof UnavailableItemsError) return
        const msg = e instanceof Error ? e.message : 'Erro ao processar pagamento'
        setPayError(msg)
        showPaymentStatusModal('error')
        toast.error(msg)
      } finally {
        setIsPaying(false)
      }
      return
    }

    if (selectedPaymentMethod === 'boleto') {
      getEvents.eventLoadObjects({
        name: 'checkout_redirect',
        category: 'checkout',
        action: 'boleto_click_redirect',
        value: String(lastOrderCreated.id)
      })
      getEvents.eventSendDataLayer({
        eventName: 'checkout_redirect',
        method: 'boleto',
        trigger: 'pay_click',
        orderId: lastOrderCreated.id,
        target: skipDocuments
          ? `/detalhes-do-pedido/${lastOrderCreated.id}`
          : `/enviar-receita/${lastOrderCreated.id}`
      })
      await finalize({
        redirectTo: skipDocuments
          ? `/detalhes-do-pedido/${lastOrderCreated.id}`
          : `/enviar-receita/${lastOrderCreated.id}`,
        shouldClearCart: true,
        alsoClearOrderState: true
      })
      return
    }

    // Para pagamento com cartão único, verifica se há CVV temporário salvo
    if (selectedPaymentMethod === 'credit') {
      try {
        const raw = localStorage.getItem('@CANNECT:SELECTED_CARD_EXTERNAL_ID')
        const selectedCardId = raw || null

        // Verifica se há CVV temporário no contexto
        const cvvFromContext = selectedCardId ? temporaryCvv[selectedCardId] : undefined

        // Verifica se o CVV já está preenchido no formulário
        const formCvv = form?.getValues?.('cvv1') || ''

        // Usa o CVV temporário se disponível, senão usa o do formulário
        const cvv = cvvFromContext || formCvv

        if (cvv && selectedCardId) {
          // Se tem CVV preenchido, paga diretamente
          await handlePay(cvv)
          // Limpa o CVV temporário após pagamento bem-sucedido
          clearCvv(selectedCardId)
          return
        }
      } catch (e) {
        // Se der erro, continua para abrir modal
      }
    }

    setShowCvv(true)
  }

  const isAddressAndPersonalValid = () => {
    const a = addressData || {}
    const required = [
      a.street,
      a.number,
      a.neighborhood,
      a.city,
      a.state,
      a.cep,
      a.fullName,
      a.cpf,
      a.email,
      a.birth,
      a.phone,
      a.gender
    ]

    // Adicionar civil_status à validação se deliveryByAssociation for true
    if (lastOrderCreated?.deliveryByAssociation) {
      required.push(a.civil_status)
    }

    const cepIsValid = (a.cep || '').replace(/\D/g, '').length === 8

    return cepIsValid && required.every((v) => (typeof v === 'string' ? v.trim().length > 0 : !!v))
  }

  const handleSaveAddressAndContinue = async () => {
    if (tazBlocked || hasUnavailableItems) return

    try {
      setSavingAddress(true)

      const payload = {
        documents: false,
        recipes: [null],
        shippingAddress: addressData
          ? {
              address_number: addressData.number || '1',
              cep: addressData.cep || '',
              state: addressData.state || '',
              street: addressData.street || '',
              district: addressData.neighborhood || '',
              city: addressData.city || '',
              is_discrete_shipping: !!addressData.is_discrete_shipping,
              complement: addressData.complement || ''
            }
          : {
              address_number: '1',
              cep: '00000-000',
              state: 'SP',
              street: 'Rua Exemplo',
              district: 'Bairro Exemplo',
              city: 'Cidade Exemplo',
              is_discrete_shipping: false
            },
        billingAddress: {},
        items: itens,
        customer: {
          name: addressData?.fullName || lastOrderCreated?.person?.name || lastOrderCreated?.person?.full_name || '',
          email: addressData?.email || lastOrderCreated?.person?.email || '',
          cpf: clearMask(addressData?.cpf || lastOrderCreated?.person?.cpf || ''),
          birthday: (() => {
            const birth = addressData?.birth || ''
            const br = /^(\d{2})\/(\d{2})\/(\d{4})$/u.exec(birth)
            if (br) return `${br[3]}-${br[2]}-${br[1]}`
            if (/^\d{4}-\d{2}-\d{2}$/u.test(birth)) return birth
            return birth ? new Date(birth).toISOString().slice(0, 10) : null
          })(),
          gender: addressData?.gender === 'M' || addressData?.gender === 'F' ? addressData.gender : null,
          phone: clearMask(addressData?.phone || lastOrderCreated?.person?.phone || lastOrderCreated?.person?.mobile || ''),
          phone_residencial: '',
          ...(addressData?.civil_status && { civil_status: addressData.civil_status })
        },
        utmParams: {},
        ...(isPublicCart
          ? {
              person_id: lastOrderCreated?.person?.id,
              phone: clearMask(addressData?.phone || lastOrderCreated?.person?.phone || '')
            }
          : {})
      }

      const cartEndpoint = isPublicCart ? 'order_cart_public' : 'order_cart'
      const { data } = await api.put(`/${cartEndpoint}/${lastOrderCreated.id}`, payload)

      if (data?.success || data?.order) {
        if (data?.order) {
          setOrderData((prev: any) => ({
            ...prev,
            cep: data.order.cep ?? prev?.cep,
            shipping_restriction: data.order.shipping_restriction ?? null,
            has_unavailable_items: data.order.has_unavailable_items ?? prev?.has_unavailable_items,
            unavailable_items: data.order.unavailable_items ?? prev?.unavailable_items,
            estimated_delivery_business_days: data.order.estimated_delivery_business_days ?? null,
            estimated_delivery_range_start: data.order.estimated_delivery_range_start ?? null,
            estimated_delivery_range_end: data.order.estimated_delivery_range_end ?? null,
            estimated_delivery_incomplete: data.order.estimated_delivery_incomplete ?? null,
            estimated_delivery_pill: data.order.estimated_delivery_pill ?? null,
            estimated_delivery_others: data.order.estimated_delivery_others ?? null
          }))
        }

        if (data?.order?.shipping_restriction?.covered === false) {
          toast.error('Não conseguimos entregar na região do CEP informado.')
          return
        }

        toast.success('Dados salvos com sucesso!')
        onNextStep?.()
      } else {
        throw new Error('Erro ao salvar dados')
      }
    } catch (error: any) {
      console.error('Erro ao salvar dados:', error)
      const errorMsg = error?.response?.data?.errors || 'Erro ao salvar dados do endereço'
      toast.error(errorMsg)
    } finally {
      setSavingAddress(false)
    }
  }

  const getProductImage = (product: any) => {
    const isNationalProduct = product?.product?.supplier?.id === 18
    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const imgMedia = product.product.media.find((m: any) => m.format === 'IMG')?.path
    const shapeNameFormatted = product?.product?.shape_id
    const isSupplementProduct = product?.product?.class_id === 6

    const isPatientUserType = role === 1

    let imageSource = ''

    switch (shapeNameFormatted) {
      case 1:
        imageSource =
          isPatientUserType && !isSupplementProduct ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        break
      case 2:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCapsule
            : imgMedia || cannectDefaultProductCapsule
        break
      case 4:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductCream
            : imgMedia || cannectDefaultProductCream
        break
      case 6:
        imageSource =
          isPatientUserType && !isSupplementProduct
            ? cannectDefaultProductGummy
            : imgMedia || cannectDefaultProductGummy
        break
      default:
        imageSource = isPatientUserType && !isSupplementProduct ? defaultImage : imgMedia || defaultImage
        break
    }

    return imageSource
  }

  if (loading) {
    return (
      <div className="ja">
        <div className="space-y-7">
          {/* Loading do produto */}
          <div className="space-y-3">
            <div className="h-20 w-20 animate-pulse rounded bg-gray-200" />
            <div className="space-y-2">
              <div className="h-4 w-48 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-32 animate-pulse rounded bg-gray-200" />
              <div className="h-5 w-24 animate-pulse rounded bg-gray-200" />
            </div>
          </div>

          <div className="h-6 w-32 animate-pulse rounded bg-gray-200" />
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center justify-between">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="h-4 w-16 animate-pulse rounded bg-gray-200" />
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <div className="h-10 w-full animate-pulse rounded bg-gray-200" />
            <div className="h-16 w-full animate-pulse rounded bg-gray-200" />
          </div>
          <div className="flex w-full justify-center">
            <div className="h-8 w-32 animate-pulse rounded bg-gray-200" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="h-auto w-full rounded-xl border border-gray-200 bg-white p-4 shadow-sm md:p-8">
      <div className="space-y-7">
        <Sumary itens={itens} importCost={calculatedImportCost} selectedPaymentMethod={selectedPaymentMethod || ''} />

        <CouponSection
          onApplyCoupon={(onApplyCoupon as any) || (() => {})}
          onRemoveCoupon={onRemoveCoupon}
          coupon={coupon}
          itens={itens}
          isUpdating={isUpdate}
          blocked={tazBlocked || hasUnavailableItems}
        />

        {currentStep === 2 && (
          <button
            onClick={handleSaveAddressAndContinue}
            disabled={
              isUpdate ||
              savingAddress ||
              !isAddressAndPersonalValid() ||
              hasUnavailableItems ||
              tazBlocked ||
              validatingDeliveryCep
            }
            className="h-10 w-full cursor-pointer rounded border-none bg-primary-800 px-16 py-2 text-base font-semibold text-white transition-colors hover:bg-[#5a6b2f] disabled:cursor-not-allowed disabled:bg-gray-400">
            {savingAddress ? 'Salvando...' : 'Continuar'}
          </button>
        )}

        {currentStep === 3 && (
          <button
            onClick={onNextStep}
            disabled={isUpdate || hasUnavailableItems || tazBlocked}
            className="h-10 w-full cursor-pointer rounded border-none bg-primary-800 px-16 py-2 text-base font-semibold text-white transition-colors hover:bg-[#5a6b2f] disabled:cursor-not-allowed disabled:bg-gray-400">
            Confirmar Pagamento
          </button>
        )}

        {currentStep === 4 && selectedPaymentMethod !== 'pix' && (
          <button
            onClick={handlePayClick}
            disabled={isUpdate || isPaying || !hasSavedCards || isCheckingCards || hasUnavailableItems || tazBlocked}
            className="h-10 w-full cursor-pointer rounded border-none bg-primary-800 px-16 py-2 text-base font-semibold text-white transition-colors hover:bg-[#5a6b2f] disabled:cursor-not-allowed disabled:bg-gray-400">
            {(() => {
              if (isPaying) return 'Processando...'
              if (isCheckingCards) return 'Verificando...'
              return 'Pagar'
            })()}
          </button>
        )}

        <div className="flex w-full justify-center">
          <PaymentMethods />
        </div>

        {currentStep !== 2 && (
          <div className="pt-4">
            <hr className="h-[0.2px] border-none bg-gray-200" />
          </div>
        )}

        {validItems.length > 0 && currentStep !== 2 && (
          <div className="space-y-3">
            <h3 className="text-base font-semibold text-gray-900">Confira seu carrinho</h3>
            {validItems.map((item: any) => (
              <div
                key={`${item.id}-${item.product?.name}`}
                className="flex items-start gap-4 rounded-lg border border-gray-200 p-4">
                <img
                  src={getProductImage(item)}
                  alt={item.product?.translated_name || 'Produto'}
                  className="h-16 w-16 rounded-lg border border-gray-200 object-cover"
                />
                <div className="flex-1 space-y-1">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {item.product?.translated_name || 'Nome do Produto'}
                  </h4>
                  <div className="md:space-y-1">
                    <div className="text-[11px] text-neutral-500 md:mb-1">
                      <span>Qtd: {item.quantity || 0}</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-1 md:gap-2">
                      {item.product?.price && item.product?.real_price && item.promotion_id && (
                        <>
                          <span className="text-[11px] italic text-neutral-500 line-through">
                            {formatReal(item.unitPrice * item.quantity)}
                          </span>
                          <span className="text-[11px] italic text-primary-500 line-through">
                            {item.promotion.discount_type === 'percent'
                              ? `- ${item.promotion.discount_value}%`
                              : `- R$ ${item.promotion.discount_value}`}
                          </span>
                        </>
                      )}
                      <span className="text-sm font-semibold text-neutral-800">
                        {formatReal(item.amount || '0,00')}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        <CaptureCvvModal isOpen={showCvv} onClose={() => setShowCvv(false)} onConfirm={handlePay} />
        <UnavailableProductModal
          isOpen={isUnavailableModalOpen}
          products={unavailableProducts}
          isLoading={isRemoving}
          onClose={closeUnavailableModal}
          onContinue={closeUnavailableModal}
        />
        {showPaymentModal && (
          <PaymentModal
            status={paymentStatus}
            onClose={() => setShowPaymentModal(false)}
            onAction={handlePaymentModalAction}
            isAllSupplements={isAllSupplements}
            isAllGeneticTests={isAllGeneticTests}
            skipDocuments={skipDocuments}
          />
        )}
        <Modal isOpen={showPixModal} setOpenModal={() => setShowPixModal(false)}>
          <div className="h-[547px] w-full p-4">
            <PixPayment
              selectedMethod="pix"
              cartItems={itens}
              code=""
              prefetchedTransaction={(window as any).PREFETCHED_PIX}
              autoGenerate={false}
              isFromModal
            />
          </div>
        </Modal>
        <Modal isOpen={showBoletoModal} setOpenModal={() => setShowBoletoModal(false)}>
          <div className="h-fit w-full p-4">
            <BoletoPayment
              selectedMethod="boleto"
              cartItems={itens}
              code=""
              prefetchedTransaction={(window as any).PREFETCHED_BOLETO}
              autoGenerate={false}
              isFromModal
            />
          </div>
        </Modal>
      </div>
    </div>
  )
}
